import { useQuery } from '@apollo/client';
import get from 'lodash.get';
import { useCallback, useMemo, useState } from 'react';
import { gql } from 'graphql-tag';
import { ResourceAllocationStatus } from '~/types';
import { specificResourceAllocationFragment } from '~/modules/resourcing/common/fragments';

export const removeDuplicatesAndMerge = (prev, { fetchMoreResult }) => {
  if (!fetchMoreResult) return prev;

  const existingAllocationIds = prev.resourceAllocations.resourceAllocations.map(
    r => r.id
  );
  const newAllocations = fetchMoreResult.resourceAllocations.resourceAllocations.filter(
    r => !existingAllocationIds.includes(r.id)
  );

  return {
    ...prev,
    resourceAllocations: {
      ...prev.resourceAllocations,
      resourceAllocations: [
        ...prev.resourceAllocations.resourceAllocations,
        ...newAllocations
      ],
      nextPageCursor: fetchMoreResult.resourceAllocations.nextPageCursor
    }
  };
};

export const getFilterInput = ({ searchText, roleUris, userIds }) => {
  const hasFilter =
    Boolean(searchText) || Boolean(roleUris) || Boolean(userIds);

  return hasFilter
    ? {
        roles: roleUris,
        searchText,
        users: userIds
      }
    : undefined;
};

export const ALLOCATIONS_MOBILE_QUERY = gql`
  query Eager_GetAllocations_MobileView(
    $projectUri: String
    $allocationStatusList: [ResourceAllocationStatus]
    $limit: Int
    $cursor: String
    $sort: ResourceAllocationSort
    $filter: ResourceAllocationFilter
  ) {
    resourceAllocations(
      projectUri: $projectUri
      limit: $limit
      cursor: $cursor
      allocationStatusList: $allocationStatusList
      sort: $sort
      filter: $filter
    ) {
      resourceAllocations {
        ...SpecificResourceAllocation
        role {
          uri
          id
          displayText
        }
      }
      nextPageCursor
    }
  }
  ${specificResourceAllocationFragment}
`;

const useCompletedProjectResourceAllocations = ({
  projectUri,
  sort,
  searchText,
  roleUris,
  userIds
}) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const variables = useMemo(
    () => ({
      projectUri,
      limit: 50,
      allocationStatusList: [ResourceAllocationStatus.Committed],
      filter: getFilterInput({ searchText, roleUris, userIds }),
      sort
    }),
    [projectUri, searchText, roleUris, userIds, sort]
  );

  const { loading, data, fetchMore } = useQuery(ALLOCATIONS_MOBILE_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  const nextPageCursor = get(data, 'resourceAllocations.nextPageCursor');
  const hasMoreRows = Boolean(nextPageCursor);

  const loadMoreRows = useCallback(async () => {
    if (loading || loadingMore || !hasMoreRows) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        query: ALLOCATIONS_MOBILE_QUERY,
        variables: {
          ...variables,
          cursor: nextPageCursor
        },
        updateQuery: removeDuplicatesAndMerge
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMoreRows, fetchMore, variables, nextPageCursor]);

  return {
    loadingRows: loading,
    resourceAllocations:
      get(data, 'resourceAllocations.resourceAllocations') || [],
    loadMoreRows,
    hasMoreRows
  };
};

export default useCompletedProjectResourceAllocations;
